import { isbot } from 'isbot';
import { disablePageScroll, enablePageScroll } from 'scroll-lock';
import { hide as hideOverlay, show as showOverlay } from 'utils/overlay';

type Shop = {
  current: boolean;
  url: string;
  name: string;
  redirect?: string;
  icon?: string | null;
  code?: string;
  use_continental?: boolean;
};

class ShopSwitch {
  public readonly currentShop: Shop;

  private readonly shops: Shop[];

  private userData: any;

  private container: HTMLElement;

  constructor(shops: Shop[], element: string | HTMLElement) {
    this.container = typeof element === 'string' ? document.querySelector(element) : element;
    this.shops = shops;
    this.currentShop = shops.find(
      (shop) =>
        shop.current ||
        shop.url.includes(window.location.origin) ||
        shop.url.includes(Shopify.shop),
    );

    this.init();
  }

  init() {
    if (this.shouldShow()) {
      this.getUserLocation().then((response) => {
        this.userData = response;
        if (!this.isOnPreferred()) {
          this.open();
        } else {
          localStorage.setItem('geo:dismissed', 'true');
        }
      });
    }
  }

  async open() {
    showOverlay();
    disablePageScroll(this.container);
    this.container.hidden = false;
  }

  async close() {
    enablePageScroll(this.container);
    if (!localStorage.getItem('geo:dismissed')) localStorage.setItem('geo:dismissed', 'true');
    hideOverlay();
    this.container.hidden = true;
  }

  // eslint-disable-next-line class-methods-use-this
  async getUserLocation() {
    const location = await fetch('https://get.geojs.io/v1/ip/geo.json');
    return location.json();
  }

  shouldShow(): boolean {
    return (
      window.location.hostname !== 'checkout.shopify.com' &&
      !isbot(navigator.userAgent) &&
      !localStorage.getItem('geo:dismissed') &&
      this.shops.length > 0
    );
  }

  getMatchingStore(): Shop {
    const europeanUnion = [
      'AT',
      'BE',
      'BG',
      'CY',
      'CZ',
      'DE',
      'DK',
      'EE',
      'ES',
      'FI',
      'FR',
      'GR',
      'HR',
      'HU',
      'IE',
      'IT',
      'LT',
      'LU',
      'LV',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SE',
      'SI',
      'SK',
    ];
    const regionalMatch = this.shops.find(
      (shop) =>
        shop.code === this.userData.country_code ||
        (shop?.redirect?.length && shop?.redirect.includes(this.userData.country_code)),
    );

    const euMatch = this.shops.find((shop) => {
      if (shop.code === 'EU') {
        if (shop.use_continental && this.userData.continent_code === 'EU') {
          return true;
        }

        if (europeanUnion.includes(this.userData.country_code)) {
          return true;
        }
      }

      return false;
    });

    const internationMatch = this.shops.find((shop) => shop.code === 'INT');

    return regionalMatch || euMatch || internationMatch;
  }

  highlightActive(matchingStore): void {
    const input: HTMLInputElement = this.container.querySelector(
      `input[data-code="${matchingStore.code}"]`,
    );

    if (input) {
      input.click();
      const flagElm: HTMLElement = this.container.querySelector('[data-flag]');
      const descriptionElm: HTMLElement = this.container.querySelector('[data-description]');
      flagElm.innerHTML = `<img src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=="
               data-src="${matchingStore?.icon}"
               class="lazyload h-5 w-auto"
               alt="${matchingStore.name} icon">
          <span class="ml-2 text-xl">${matchingStore.name}</span>`;

      descriptionElm.innerHTML = matchingStore.description;
    }
  }

  isOnPreferred(): boolean {
    const matchingStore = this.getMatchingStore();
    this.highlightActive(matchingStore);

    return (
      this.currentShop.code === matchingStore.code ||
      this.currentShop?.redirect?.includes(this.userData.country_code)
    );
  }
}

export default (config: Shop[], element: string | HTMLElement) => new ShopSwitch(config, element);
